// services/school-ui/src/pages/promotion/UndoBanner.tsx
// Toast-style overlay with 5-second countdown. Mounted at page root.
// Calls onUndo when the button is clicked; calls onTimeout when the
// countdown elapses. Owner clears its own pendingUndo via either
// callback.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PendingUndo } from "./types";

interface Props {
  pending: PendingUndo;
  onUndo: () => void;
  onTimeout: () => void;
  windowMs?: number; // default 5000
}

export function UndoBanner({ pending, onUndo, onTimeout, windowMs = 5000 }: Props): JSX.Element {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(windowMs);

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const left = Math.max(0, windowMs - elapsed);
      setRemaining(left);
      if (left === 0) {
        clearInterval(tick);
        onTimeout();
      }
    }, 100);
    return () => clearInterval(tick);
    // pending.decidedAt changes when a NEW decision happens, which
    // remounts this banner anyway via key= on the parent side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pct = (remaining / windowMs) * 100;
  const labelKey =
    pending.action === "approve" ? "promotion.undo.approved" : "promotion.undo.rejected";

  return (
    <div className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded border border-slate-200 bg-slate-50 px-4 py-2 text-xs shadow-lg">
      <span className="text-slate-500">{t(labelKey)}</span>
      <button
        onClick={onUndo}
        className="rounded bg-blue-100 px-2 py-0.5 text-blue-600 hover:bg-blue-200"
      >
        {t("promotion.undo.action")}
      </button>
      <div className="h-1 w-16 overflow-hidden rounded bg-slate-100">
        <div
          className="h-full bg-blue-600 transition-[width] duration-100"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
